// 미리보기 **모사 축** 파생 — 편집 중 템플릿 → **후보 목록**.
//
// 결정 기록: docs/decisions/2026-09-01-composer-preview-simulation-axes.md §①·③-2·④.
//
// ## 후보 기준: `lifecycle:'live'` 바인딩이 읽는 스칼라
//
// 문제의 본질이 **실시간 데이터**다. live 바인딩이 읽는 값은 **보고서가 만들어진 뒤에 쌓이는
// 것**이라 컨텍스트 기반 미리보기로는 영영 못 본다 — 정확히 모사가 필요한 집합이다. 반대로
// snapshot·컨텍스트 resolve로 채워지는 값은 이미 에디터에서 보이므로 모사할 이유가 없다.
//
// 이 기준이 셋을 없앤다(결정 §①):
//   1. **수동 선언이 불필요하다** — 후보가 코드에서 파생되므로 선언 누락으로 인한 silent fail이
//      원천적으로 없다(피처 플래그 미등록과 같은 계열의 사고를 미리 막는다).
//   2. **"그 템플릿이 참조하는가"가 내포된다** — 템플릿의 live 바인딩을 순회하므로 보고서 타입별
//      구분이 저절로 성립한다. 별도 참조 판정을 rc로 승격할 필요가 없다.
//   3. **기준이 자의적이지 않다** — "실시간으로 들어오는 값이라 저작 시점엔 없는 것".
//
// ⚠️ `carrierPair`는 같은 `(catalogKey, select)`를 snapshot+live 쌍으로 만든다 — **중복 제거**하고,
//    판정은 "그 쌍에 live가 하나라도 있으면 후보"다(그래서 live만 모으면 저절로 그렇게 된다).
// ⚠️ 실익이 낮아 보이는 것(`tbm-conductor.signatureData` 등)도 후보에 들어온다. **배제 기준을
//    만들지 않는다** — 자의적 예외는 유지 대상이 되고, 실익 판단은 저작자 몫이다.
//
// ## 왜 순수 함수 모듈인가
//
// 축 목록을 서버가 내리면 **편집 중 템플릿**을 실어 보내는 왕복이 새로 생긴다. 노출 기준은
// 저장본이 아니라 편집본이어야 하고(방금 정책을 켠 저작자가 저장 없이 확인할 수 있어야 한다),
// admin은 카탈로그 entries를 이미 통째로 갖고 있다(`getReportComposerCatalog`).
//
// 그리고 **모달은 도메인을 모른다**는 성질은 파생 위치와 무관하다 — 파생을 여기로 떼어 놓았으므로
// `PreviewContextBar`는 `label`·`widget`·`options`만 보고 그린다. 분할이든 다회차든 이름조차 없다.
//
// ⚠️ 이 목록은 **후보**다(§③-2). 섹션은 빈 상태로 시작하고 저작자가 고른 축만 행으로 쌓인다.
// 그래야 부수효과가 있는 `plan` 소스 조회가 "저작자가 그 축을 추가하는 순간"에 구조적으로 묶인다.

import { walkBindings } from '@safetyhub/report-composer';
import { parseLocalToUTC } from '@safetyhub/shared';
import {
  buildCatalogIndex,
  PREVIEW_SIMULATION_APPROVAL_SIGNED_AXIS_KEY,
  PREVIEW_SIMULATION_SPLIT_AXIS_KEY,
  rowCountSimulationAxisKey,
  VALUE_SETS,
  type CatalogEntry,
  type CatalogField,
  type ComposerNode,
  type PreviewSimulationAxis,
  type PreviewSimulationOption,
  type ReportTemplate,
} from '@safetyhub/shared';

/**
 * 모사 축 파생 입력 중 템플릿에서 실제로 읽는 부분 — 본문 바인딩·분할 정책·데이터 소스 선언
 * (행 수 축은 readonly 조립 list 소스에서 파생된다)·결재라인 구조(결재 서명 단계 축의 파생
 * 조건, 2026-09-04)뿐이다.
 */
export type SimulationAxisTemplate = Pick<
  ReportTemplate,
  'body' | 'policy' | 'dataSources' | 'approvalLineMeta'
>;

/** boolean 축의 두 선택지 — 라벨은 축 이름이 뜻을 지므로 여기선 예/아니오면 충분하다. */
const BOOLEAN_OPTIONS: PreviewSimulationOption[] = [
  { label: '예', value: true },
  { label: '아니오', value: false },
];

/**
 * 편집 중 템플릿에서 모사 축 **후보 목록**을 파생한다.
 *
 * 출처 넷:
 *  - **live 바인딩이 읽는 카탈로그 필드** — 위 헤더의 기준. 단수(single) entry의 최상위 스칼라
 *    필드만 축이 된다(중첩 경로는 범위 밖, `object`/`array`는 §③-2로 제외).
 *  - **`reportScoped: true` 카탈로그 entry의 스칼라 필드**(2026-09-04 자동연결 폐기) — 보고서
 *    문서 자체의 값(제목·차수·결재 등)이라 **lifecycle 불문**(snapshot 바인딩 포함) 후보.
 *    live-만 규칙의 전제 "snapshot은 이미 보인다"가 이 entry들엔 성립하지 않는다. 그룹은
 *    'report'로 일원(live로도 잡히는 중복 포함 — 축 그룹은 entry가 정한다). 여기서 결재란은
 *    필드 축이 아니라 별도의 **결재 서명 단계 축**(아래)으로 다룬다.
 *  - **템플릿 정책** — `policy.split.provider`가 있으면 분할 축이 파생된다. 여기 있는 것은
 *    "정책이 있나"뿐이라, 분할을 쓰지 않는 보고서 타입에는 **아무것도 안 해도** 칸이 없다.
 *  - **readonly 조립 list 소스**(2026-09-03 확장) — 본문 반복이 소비하는 것마다 「행 수」 축
 *    하나. 행 *내용*은 여전히 값 하나로 모사할 수 없어 닫혀 있고, 행 *수*만 연 것이다.
 *  - (+) **결재라인 구조**(2026-09-04) — approvalLineMeta가 있고 본문에 결재란 노드가 있으면
 *    「결재 서명 단계」 축(`PREVIEW_SIMULATION_APPROVAL_SIGNED_AXIS_KEY`, number N) 하나.
 *
 * @param catalog admin이 이미 보유한 카탈로그 entries(`getReportComposerCatalog` 응답)
 * @param template **편집 중** 템플릿(저장본 아님)
 */
export function derivePreviewSimulationAxes(
  catalog: CatalogEntry[],
  template: SimulationAxisTemplate | undefined,
): PreviewSimulationAxis[] {
  const axes: PreviewSimulationAxis[] = [];

  // 분할 축 — 「생성 옵션」 그룹. 바인딩이 아니라 템플릿 **정책** 파생이라 축 성질이 다르다
  // (보고서 생성 *시점*에 정해지는 값 vs live 바인딩의 생성 *후* 쌓이는 값).
  if (template?.policy?.split?.provider) {
    axes.push({
      key: PREVIEW_SIMULATION_SPLIT_AXIS_KEY,
      label: '분할 그룹',
      description:
        '보고서 생성 시점에 정해지는 분할 그룹을 미리 골라 그 그룹 기준으로 그려 봅니다. ' +
        '선택지는 고른 점검의 분할 계획에서 가져오며, 미지정이면 통합(전체)입니다.',
      group: 'generation',
      widget: 'select',
      // ⚠️ 계획 조회에는 **부수효과가 있다**(TbmMeta upsert + AI 정제 캐시 생성) — 옵션을 빈 채로
      //    두고, 저작자가 이 축을 추가하는 조작에서만 1회 채운다(§④). 선언에 명시해 두는 이유는
      //    다음 축을 붙이는 사람이 규칙만으로는 모르기 때문이다.
      optionsSource: ['plan'],
      payloadKind: 'opaque',
      unsetLabel: '통합(전체)',
    });
  }

  const index = buildCatalogIndex(catalog);
  // 트리 순서 유지 + 중복 제거(carrierPair·같은 값을 여러 자리에서 읽는 경우).
  const seen = new Set<string>();
  for (const { catalogKey, select, live } of collectScalarBindingRefs(template)) {
    const key = `${catalogKey}.${select}`;
    if (seen.has(key)) continue;
    const entry = index[catalogKey];
    if (!entry) continue;
    // 단수 entry의 **최상위** 필드만 — list 행이나 중첩 경로는 스칼라 하나로 모사할 수 없다.
    if (entry.cardinality !== 'single') continue;
    // 두 번째 출처(2026-09-04 자동연결 폐기): `reportScoped: true` entry는 **lifecycle 불문**
    // (snapshot 바인딩 포함) 후보다 — live-만 규칙의 전제 "snapshot은 이미 보인다"가 이 entry들엔
    // 성립하지 않는다(보고서 문서 자체의 값이라 저작 미리보기엔 박제 사건이 없다). 판정은 admin
    // 하드코딩 목록이 아니라 카탈로그 선언 메타(CatalogEntryBase.reportScoped JSDoc이 정본).
    // live 바인딩으로도 잡히는 중복은 dedup(seen)에 의해 축 하나로 남고, 그룹은 entry가 정하므로
    // 'report'로 일원된다.
    const reportScoped = entry.reportScoped === true;
    if (!reportScoped && !live) continue;
    const field = entry.returns.find((f) => f.path === select);
    if (!field) continue;
    const shape = deriveScalarShape(field);
    if (!shape) continue; // object/array — 직접 입력이 무리라 후보에서 제외(§③-2)
    seen.add(key);
    axes.push({
      key,
      label: field.label,
      // 파생이 아는 구조 사실로 합성한다 — entry 라벨(어느 데이터의 값인가)과 그 축의 성질
      // (왜 저작 중에는 안 보이는가). 카탈로그 필드에는 사람용 설명이 따로 없다.
      description: reportScoped
        ? `「${entry.label}」의 ${field.label} 값을 지정합니다. ` +
          '보고서 문서 자체의 값이라 저작 중에는 문서가 없습니다. ' +
          '미지정이면 계산 기본값(계산 불가 값은 빈값)으로 표시됩니다.'
        : `「${entry.label}」의 ${field.label} 값을 지정합니다. ` +
          '보고서가 만들어진 뒤 실시간으로 쌓이는 값이라 저작 중에는 실데이터가 없을 수 있습니다. ' +
          '미지정이면 실데이터 그대로 표시됩니다.',
      group: reportScoped ? 'report' : 'live',
      payloadKind: 'scalar',
      unsetLabel: reportScoped ? '계산 기본값' : '실데이터 그대로',
      ...shape,
    });
  }

  // 결재 서명 단계 축(2026-09-04 자동연결 폐기 §2) — 값 N이면 결재란 1..N 단계를 표본 서명으로
  // 채워 본다(적용은 클라 applySimulatedApproval → rc buildSimulatedApprovalData.signedLevel).
  // 파생 조건 판정(코드 확인 근거):
  //  - 주입 채널이 buildSimulatedApprovalData(approvalLineMeta 골격)라 **meta가 없으면 축이
  //    닿을 곳이 없다**(applySimulatedApproval이 meta 부재 시 그대로 반환). meta 없는 결재란
  //    노드의 「담당」 1칸은 ApprovalBlockSpecial의 표시 전용 합성이라 주입 채널 밖이다.
  //  - 그릴 칸(고정 단계 + 동적 슬롯)이 0이면 서명할 자리가 없다.
  //  - 본문에 approvalBlock special 노드가 없으면 채워 봐야 화면이 안 달라진다(행 수 축의
  //    "반복이 곧 소비다"와 같은 게이트 — 수집은 아래 본문 순회에 동승, 순회 복제 없음).
  const bodyFacts = collectBodyConsumption(template);
  const meta = template?.approvalLineMeta;
  const approvalCellCount =
    (meta?.defaultApprovalLine?.length ?? 0) + Math.max(0, meta?.maxAdditionalLines ?? 0);
  if (meta && approvalCellCount > 0 && bodyFacts.hasApprovalBlock) {
    axes.push({
      key: PREVIEW_SIMULATION_APPROVAL_SIGNED_AXIS_KEY,
      label: '결재 서명 단계',
      description:
        '결재란을 지정한 단계까지 표본 서명(「서명」 표기 이미지)으로 채워 봅니다. ' +
        '결재 서명은 보고서 문서 자체의 값이라 저작 중에는 문서가 없습니다. ' +
        '미지정이면 빈 결재란(미서명)입니다.',
      group: 'report',
      widget: 'number',
      optionsSource: ['manual'],
      payloadKind: 'scalar',
      unsetLabel: '빈 결재란(미서명)',
    });
  }

  // 행 수 모사 축 — **readonly 조립 list 소스** 중 본문 repeat/표 반복영역이 소비하는 것.
  // 결정 §③-2의 "list 소스 제외"를 사용자 결정(2026-09-03 — 시연을 위해 세션 여러 개 전개가
  // 모사로 가능해야 한다)으로 이 좁은 형태만 연 것이다: 값 하나로 모사할 수 없는 것은 행
  // *내용*이지 행 *수*가 아니다. 서버는 실데이터 행을 순환 복제해 늘려 보여 주고, 빈 소스는
  // 카탈로그 `returns` 형상에서 파생한 표본 행을 씨앗으로 쓴다(2026-09-03 fabrication 확장 —
  // 서버 `applyPreviewSimulationRowCounts` JSDoc이 정본).
  //
  // 그룹은 'live'다 — 늘려 보는 대상(세션 행 등)이 보고서 생성 **후** 쌓이는 실시간 데이터이고,
  // 그 소스의 소비 채널 자체가 live 바인딩(repeat 자신의 lifecycle:'live')이라 기존 어휘 그대로다.
  //
  // 편집형(editable) 소스를 제외하는 이유: 그쪽 행은 작성폼 초안(seed)으로 노출돼 저작자가
  // 이미 행을 직접 늘려 볼 수 있다 — 모사가 필요한 것은 손댈 수 없는 표시 전용 소스뿐이다.
  const overKeys = bodyFacts.repeatOverKeys;
  const seenRowCount = new Set<string>();
  for (const def of template?.dataSources ?? []) {
    if (def.kind !== 'assembled') continue;
    if (def.cardinality !== 'list') continue;
    if (def.editMode !== 'readonly') continue;
    if (!overKeys.has(def.key)) continue;
    // 같은 카탈로그 entry를 여러 선언이 소비해도 축은 하나다(서버 확장 대상이 entry 행 배열이라
    // 어차피 한 번에 같이 늘어난다). 라벨은 트리 순서상 첫 선언의 것.
    if (!def.primary || seenRowCount.has(def.primary)) continue;
    const entry = index[def.primary];
    if (!entry || entry.cardinality !== 'list') continue;
    seenRowCount.add(def.primary);
    axes.push({
      key: rowCountSimulationAxisKey(def.primary),
      label: `${def.label} 행 수`,
      description:
        `「${def.label}」 목록을 지정한 행 수만큼 늘려 봅니다. ` +
        '실데이터 행이 있으면 순환 복제하고, 없으면 표본 행({필드 이름} 표기)으로 시작합니다. ' +
        '실제 행 수보다 적게 지정하면 줄이지 않습니다.',
      group: 'live',
      widget: 'number',
      optionsSource: ['manual'],
      payloadKind: 'scalar',
      unsetLabel: '실데이터 그대로',
    });
  }

  return axes;
}

/**
 * 본문 트리 소비 사실 한 순회 수집 — 반복이 소비하는 소스 key(repeat 노드의 `over` + 표 노드의
 * `repeatRegions[].over`)와 approvalBlock special 노드 존재 여부.
 *
 * 행 수 축은 "그 소스가 실제로 행 반복으로 그려지는가"를 파생 조건으로 삼는다 — 반복 없이 flat
 * 바인딩으로만 읽히는 list 소스의 행 수를 늘려 봐야 화면에 아무것도 안 달라진다. 결재 서명 단계
 * 축도 같은 논리다: 본문에 결재란 노드가 없으면 채워 봐야 화면이 안 달라진다(같은 순회에 동승 —
 * 순회를 축마다 복제하면 저작 어휘가 늘 때 한쪽만 조용히 낡는다).
 *
 * ⚠️ 행 스코프 중첩 반복(예: 세션 행 안 `over:'findings'` — 행 **필드**를 도는 repeatRegions)의
 * over도 수집되지만, 매칭 축이 dataSource `key`라 필드명과 우연히 겹치지 않는 한 무해하다
 * (겹쳐도 "그 이름의 소스가 소비된다"는 판정이 과하게 참이 될 뿐, 없는 축이 생기지는 않는다).
 */
function collectBodyConsumption(
  template: SimulationAxisTemplate | undefined,
): { repeatOverKeys: Set<string>; hasApprovalBlock: boolean } {
  const keys = new Set<string>();
  let hasApprovalBlock = false;
  const visit = (nodes: ComposerNode[]): void => {
    for (const node of nodes) {
      switch (node.kind) {
        case 'group':
        case 'stack':
          visit(node.children);
          break;
        case 'repeat':
          keys.add(node.over);
          visit([node.template]);
          break;
        case 'branch':
          for (const slot of node.branches) visit(slot.children);
          break;
        case 'table':
          for (const region of node.repeatRegions ?? []) keys.add(region.over);
          for (const cell of node.cells) {
            if (cell.content) visit(cell.content);
          }
          break;
        case 'special':
          if (node.special === 'approvalBlock') hasApprovalBlock = true;
          break;
        default:
          break;
      }
    }
  };
  if (template?.body) visit(template.body);
  return { repeatOverKeys: keys, hasApprovalBlock };
}

/**
 * 편집 중 템플릿 본문의 바인딩이 가리키는 `(catalogKey, select)` 쌍(+ live 여부).
 *
 * `walkBindings`(rc)가 노드·표 셀·compute 입력·branch 조건까지 한 순회로 방문한다 — 순회 구조를
 * 여기서 복제하면 저작 어휘가 늘 때마다 이 목록만 조용히 낡는다.
 *
 * lifecycle 게이트는 여기가 아니라 **호출부(entry 판정)**에 있다(2026-09-04): 비-reportScoped
 * entry는 종전대로 live 바인딩만 후보고, `reportScoped: true` entry는 snapshot 바인딩도 후보라
 * 수집 단계에서 걸러 버리면 그 축이 영영 안 나온다. 그래서 전 바인딩을 live 표식과 함께 낸다.
 *
 * 작성폼(`composerWidgetTree`)은 순회하지 않는다: 그쪽 바인딩은 작성자 입력(input)·prefill 채널이라
 * "보고서가 만들어진 뒤에 쌓이는 값"이 아니다 — 이 축이 겨냥한 집합이 아니다.
 */
function collectScalarBindingRefs(
  template: SimulationAxisTemplate | undefined,
): { catalogKey: string; select: string; live: boolean }[] {
  const refs: { catalogKey: string; select: string; live: boolean }[] = [];
  if (!template?.body) return refs;
  walkBindings(template.body, (binding) => {
    const catalogKey = binding.catalogKey;
    const select = binding.select;
    if (!catalogKey || !select) return;
    refs.push({ catalogKey, select, live: binding.lifecycle === 'live' });
  });
  return refs;
}

/**
 * 필드 타입 → 위젯·선택지. `object`/`array`면 `undefined`(=후보 제외).
 *
 * 전부 `optionsSource: ['manual']`이다 — 저작자가 값을 **직접 지정**하는 축이고, 위젯과 선택지만
 * 타입에서 갈린다. 선택지를 셀 수 있는 타입(boolean·enum)만 `options`를 갖고 나머지 스칼라는
 * 자유 입력이다 — 값 후보를 지어내지 않는다(없는 선택지를 보여 주는 것보다 빈 칸이 정직하다).
 */
function deriveScalarShape(
  field: CatalogField,
): Pick<PreviewSimulationAxis, 'widget' | 'optionsSource' | 'options'> | undefined {
  switch (field.type) {
    case 'boolean':
      return { widget: 'radio', optionsSource: ['manual'], options: BOOLEAN_OPTIONS };
    case 'enum': {
      const set = VALUE_SETS[field.valueSetId];
      if (!set) return undefined;
      return {
        widget: 'select',
        optionsSource: ['manual'],
        options: set.map((o) => ({ label: o.label, value: o.value })),
      };
    }
    case 'number':
      return { widget: 'number', optionsSource: ['manual'] };
    case 'date':
      return { widget: 'date', optionsSource: ['manual'] };
    case 'string':
      return { widget: 'text', optionsSource: ['manual'] };
    default:
      // 'object' | 'array' — 직접 입력이 무리다(§③-2).
      return undefined;
  }
}

/**
 * 축 값 설정/해제 — `undefined`면 그 축을 **지운다**(빈 맵이면 필드 자체를 없앤다).
 *
 * 키가 남은 채로 `undefined` 값을 두지 않는 이유: 서버는 범위 밖 키를 무시하지만, 클라 쪽에서
 * "모사 중인가"를 판정하는 코드(칩 표시·해제 버튼 노출)가 키 존재만 보면 거짓 양성이 된다.
 */
export function setSimulationAxisValue(
  current: Record<string, unknown> | undefined,
  axisKey: string,
  value: unknown,
): Record<string, unknown> | undefined {
  const next = { ...(current ?? {}) };
  if (value === undefined) delete next[axisKey];
  else next[axisKey] = value;
  return Object.keys(next).length > 0 ? next : undefined;
}

/**
 * 계획(`plan`) 출처 축의 값만 비운다 — **적용이 실제로 실패한 뒤** stale을 걷어낼 때 쓴다
 * (`previewContextPersistence.releasePlanSimulation`).
 *
 * 계획 산출물(행 id)은 **그 점검·그 시점**의 것이라, 다른 점검에 그대로 실으면 서버가
 * "분할 대상에 없는 항목입니다"로 400을 낸다. 스칼라 모사는 점검과 무관하므로 그대로 둔다 —
 * 저작자가 고른 갈래가 점검을 바꿨다고 조용히 풀리면 그게 더 놀랍다.
 *
 * ⚠️ 2026-09-03 전까지는 점검·조직 **변경 시점에 미리** 비웠다. 지금은 비우지 않는다 — 멀쩡한
 * 경우까지 갈래를 잃기 때문이고, 실패한 경우만 사후에 정리한다(결정 §영속의 범위).
 */
export function clearPlanSimulationAxes(
  current: Record<string, unknown> | undefined,
  axes: PreviewSimulationAxis[],
): Record<string, unknown> | undefined {
  if (!current) return undefined;
  const next = { ...current };
  for (const axis of axes) {
    if (axis.optionsSource.includes('plan')) delete next[axis.key];
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

/** 그 축이 계획 조회(부수효과)로만 값을 얻는가 — 옵션 lazy 조회·점검 변경 시 폐기의 판정축. */
export function isPlanSourcedAxis(axis: PreviewSimulationAxis): boolean {
  return axis.optionsSource.includes('plan');
}

/** 자유 입력 축의 원문 파싱 결과 — 판정 셋을 한 이름씩 갖는다(불완전은 무시, 빈 입력은 해제). */
export type SimulationAxisInputParse =
  | { kind: 'incomplete' } // 아직 값이 아니다(불완전 날짜·숫자 아님) — 입력 도중이니 무시
  | { kind: 'unset' } // 빈 입력 — 그 축의 값 해제
  | { kind: 'value'; value: unknown };

/**
 * 자유 입력 축(text/number/date)의 원문 → 실을 값 파싱 — 실데이터 반영 모달과 프리셋 편집기가
 * **같은 규칙**을 쓰도록 한 곳에 둔다(갈라지면 같은 축이 호스트마다 다른 형상으로 실린다).
 *
 * ⚠️ date는 **시간 포함 UTC로 변환해 싣는다**(CLAUDE.md 시간대 정책). 서버는 이 값을 resolved에
 *    그대로 얹고 그 자리를 읽는 것은 date 타입 카탈로그 필드라, 날짜 문자열을 날것으로 실으면
 *    다른 date 값들과 형상이 갈린다. 입력 위젯은 datetime-local(2026-09-03 사용자 지적 — 「첫
 *    세션 교육 시작/완료 일시」처럼 시각까지 뜻이 있는 date 필드가 있고, 템플릿 표시 패턴도
 *    HH:mm를 그린다)이라 raw에 시각이 실려 오고, 날짜만 오면(구 저장 라벨 등) 로컬 00:00:00.
 */
export function parseSimulationAxisInput(
  axis: PreviewSimulationAxis,
  raw: string,
): SimulationAxisInputParse {
  if (raw === '') return { kind: 'unset' };
  if (axis.widget === 'number') {
    const n = Number(raw);
    if (Number.isNaN(n)) return { kind: 'incomplete' };
    return { kind: 'value', value: n };
  }
  if (axis.widget === 'date') {
    try {
      const local = raw.includes('T') ? raw : `${raw}T00:00:00`;
      return { kind: 'value', value: parseLocalToUTC(local).toISOString() };
    } catch {
      return { kind: 'incomplete' }; // 입력 도중의 불완전한 날짜·시각(yyyy-MM, ...T1 등)
    }
  }
  return { kind: 'value', value: raw };
}
